import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { Business } from "../../../constants/business.constant";
import {
  generateTransactionSummary,
  updateCustomerBusiness,
  updateCustomerLoanRequest,
} from "../../../services/customer.service";

interface LoanSummary {
  id?: string;
  loanSize?: number;
  maxLTV?: number;
  loanType?: string;
  payoffValue?: number;
  proceeds?: { purpose: string; amount: number }[];
  guaranteedName?: string | null;
  requestedLoanProduct?: string | null;
  intendedUseOfProceeds?: string | null;
  desiredLoanTermAndAmortization?: string | null;
  guarantorOwnershipAndRoles?: string | null;
  guarantorBusinessInvolvement?: string | null;
  guarantorOutsideIncome?: string | null;
  primaryOperatorIndustryYears?: string | null;
  operatorLocationCount?: string | null;
  guarantorCreditEvents?: string | null;
  operatingBusinessLegalName?: string | null;
  operatingBusinessEin?: string | null;
  realEstateHoldingEntityLegalName?: string | null;
  realEstateHoldingEntityEin?: string | null;
  entityTypes?: string | null;
  businessNature?: string | null;
  businessTaxReturnsAvailable?: string | null;
  personalTaxReturnsAvailable?: string | null;
  otherGuarantorBusinesses?: string | null;
  grossRevenuesMostRecentYear?: number | string | null;
  approximateNetIncome?: number | string | null;
  significantInventoryValue?: number | string | null;
  underwritingAddBacks?: string | null;
  currentAnnualRent?: number | string | null;
  currentBusinessDebts?: string | null;
  existingObligationMonthlyPayments?: number | string | null;
  debtPaidOffWithTransaction?: string | null;
  holdingEntityLineOfCreditOrLiens?: string | null;
  targetClosingDateOrTimeSensitivity?: string | null;
  appraisalOrderedExpectedDelivery?: string | null;
  rentRollOrLeaseAgreementStatus?: string | null;
}

interface Props {
  open: boolean;
  business: Business | null;
  loan?: LoanSummary | null;
  onClose: () => void;
  onSaved?: () => void;
}

const emptyValues = {
  propertyType: "",
  appraisalStatus: "",
  appraiserName: "",
  currentLender: "",
  payoffBalance: "",
  propertyLiens: "",
  occupancyType: "",
  requestedLoanProduct: "",
  intendedUseOfProceeds: "",
  desiredLoanTermAndAmortization: "",
  guarantorOwnershipAndRoles: "",
  guarantorBusinessInvolvement: "",
  guarantorOutsideIncome: "",
  primaryOperatorIndustryYears: "",
  operatorLocationCount: "",
  guarantorCreditEvents: "",
  operatingBusinessLegalName: "",
  operatingBusinessEin: "",
  realEstateHoldingEntityLegalName: "",
  realEstateHoldingEntityEin: "",
  entityTypes: "",
  businessNature: "",
  businessTaxReturnsAvailable: "",
  personalTaxReturnsAvailable: "",
  otherGuarantorBusinesses: "",
  grossRevenuesMostRecentYear: "",
  approximateNetIncome: "",
  significantInventoryValue: "",
  underwritingAddBacks: "",
  currentAnnualRent: "",
  currentBusinessDebts: "",
  existingObligationMonthlyPayments: "",
  debtPaidOffWithTransaction: "",
  holdingEntityLineOfCreditOrLiens: "",
  targetClosingDateOrTimeSensitivity: "",
  appraisalOrderedExpectedDelivery: "",
  rentRollOrLeaseAgreementStatus: "",
};

const formatAddress = (business?: Business | null) =>
  [business?.street, business?.city, business?.state, business?.zipCode]
    .filter(Boolean)
    .join(", ");

const inferRequestedAmountOrLtv = (loan?: LoanSummary | null) => {
  if (!loan) return "";
  const parts = [];
  if (loan.loanSize) {
    parts.push(`$${Number(loan.loanSize).toLocaleString()}`);
  }
  if (loan.maxLTV) {
    parts.push(`${loan.maxLTV}% LTV`);
  }
  return parts.join(" / ");
};

const inferUseOfProceeds = (loan?: LoanSummary | null) => {
  if (!loan) return "";
  if (loan.intendedUseOfProceeds) {
    return loan.intendedUseOfProceeds;
  }
  const proceeds = loan.proceeds || [];
  if (loan.payoffValue && proceeds.length) {
    return `Payoff current loan and cash-out for ${proceeds
      .map((item) => item.purpose)
      .join(", ")}`;
  }
  if (loan.payoffValue) {
    return "Payoff only";
  }
  return "";
};

const PropertyCollateralQuestionsDialog: React.FC<Props> = ({
  open,
  business,
  loan,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState(0);
  const [summaryError, setSummaryError] = useState("");
  const [values, setValues] = useState(emptyValues);
  const businessName = business?.companyName || "Selected business";
  const sectionTitleSx = {
    mt: 1,
    px: 1.5,
    py: 1,
    borderRadius: 1.5,
    bgcolor: "white",
    border: "1px solid",
    borderColor: "divider",
    color: "#111827",
    fontWeight: 700,
  };

  const generationProgress = generating
    ? Math.min(95, Math.max(8, Math.round((generationElapsedSeconds / 90) * 95)))
    : 0;

  useEffect(() => {
    if (!generating) {
      setGenerationElapsedSeconds(0);
      return;
    }

    const timer = window.setInterval(() => {
      setGenerationElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [generating]);

  useEffect(() => {
    if (!open) return;
    setSummaryError("");

    setValues({
      propertyType: business?.propertyType || "",
      appraisalStatus: business?.appraisalStatus || "",
      appraiserName: business?.appraiserName || "",
      currentLender: business?.currentLender || "",
      payoffBalance: String(business?.payoffBalance || loan?.payoffValue || ""),
      propertyLiens: business?.propertyLiens || "",
      occupancyType: business?.occupancyType || "",
      requestedLoanProduct: loan?.requestedLoanProduct || loan?.loanType || "",
      intendedUseOfProceeds: inferUseOfProceeds(loan),
      desiredLoanTermAndAmortization:
        loan?.desiredLoanTermAndAmortization || "",
      guarantorOwnershipAndRoles: loan?.guarantorOwnershipAndRoles || "",
      guarantorBusinessInvolvement:
        loan?.guarantorBusinessInvolvement || "",
      guarantorOutsideIncome: loan?.guarantorOutsideIncome || "",
      primaryOperatorIndustryYears:
        loan?.primaryOperatorIndustryYears || "",
      operatorLocationCount: loan?.operatorLocationCount || "",
      guarantorCreditEvents: loan?.guarantorCreditEvents || "",
      operatingBusinessLegalName:
        loan?.operatingBusinessLegalName || business?.companyName || "",
      operatingBusinessEin:
        loan?.operatingBusinessEin || business?.taxIdEin || "",
      realEstateHoldingEntityLegalName:
        loan?.realEstateHoldingEntityLegalName || "",
      realEstateHoldingEntityEin:
        loan?.realEstateHoldingEntityEin || "",
      entityTypes: loan?.entityTypes || "",
      businessNature: loan?.businessNature || "",
      businessTaxReturnsAvailable:
        loan?.businessTaxReturnsAvailable || "",
      personalTaxReturnsAvailable:
        loan?.personalTaxReturnsAvailable || "",
      otherGuarantorBusinesses:
        loan?.otherGuarantorBusinesses || "",
      grossRevenuesMostRecentYear:
        loan?.grossRevenuesMostRecentYear?.toString() || "",
      approximateNetIncome:
        loan?.approximateNetIncome?.toString() || "",
      significantInventoryValue:
        loan?.significantInventoryValue?.toString() || "",
      underwritingAddBacks: loan?.underwritingAddBacks || "",
      currentAnnualRent: loan?.currentAnnualRent?.toString() || "",
      currentBusinessDebts: loan?.currentBusinessDebts || "",
      existingObligationMonthlyPayments:
        loan?.existingObligationMonthlyPayments?.toString() || "",
      debtPaidOffWithTransaction:
        loan?.debtPaidOffWithTransaction || "",
      holdingEntityLineOfCreditOrLiens:
        loan?.holdingEntityLineOfCreditOrLiens || "",
      targetClosingDateOrTimeSensitivity:
        loan?.targetClosingDateOrTimeSensitivity || "",
      appraisalOrderedExpectedDelivery:
        loan?.appraisalOrderedExpectedDelivery || "",
      rentRollOrLeaseAgreementStatus:
        loan?.rentRollOrLeaseAgreementStatus || "",
    });
  }, [business, loan, open]);

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const formatMoney = (value: string) => {
    if (!value) return "";
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;
    return `$${numericValue.toLocaleString()}`;
  };

  const formatOption = (value: string) => {
    const labels: Record<string, string> = {
      retail: "Retail",
      "mixed-use": "Mixed-use",
      commercial: "Commercial",
      industrial: "Industrial",
      "in-hand": "Appraisal in hand",
      expected: "Expected value",
      estimated: "Estimated value",
      "owner-occupied": "Owner-occupied",
      "tenant-occupied": "Tenant-occupied",
      both: "Both",
      conventional_cre: "Conventional CRE",
      sba_7a: "SBA 7(a)",
      sba_504: "SBA 504",
      bridge: "Bridge",
      conventional: "Conventional",
      SBA: "SBA",
      yes: "Yes",
      no: "No",
      partial: "Partial / in progress",
    };
    return labels[value] || value;
  };

  const buildSummaryData = () => ({
    businessName,
    propertyAddress: formatAddress(business),
    loanName: loan?.loanSize ? inferRequestedAmountOrLtv(loan) : "",
    loanSize: loan?.loanSize,
    maxLTV: loan?.maxLTV,
    loanType: loan?.loanType,
    payoffValue: loan?.payoffValue,
    proceeds: loan?.proceeds || [],
    guaranteedName: loan?.guaranteedName || "",
    answers: {
      ...values,
      propertyTypeLabel: formatOption(values.propertyType),
      appraisalStatusLabel: formatOption(values.appraisalStatus),
      occupancyTypeLabel: formatOption(values.occupancyType),
      requestedLoanProductLabel: formatOption(values.requestedLoanProduct),
      businessTaxReturnsAvailableLabel: formatOption(values.businessTaxReturnsAvailable),
      personalTaxReturnsAvailableLabel: formatOption(values.personalTaxReturnsAvailable),
      payoffBalanceFormatted: formatMoney(values.payoffBalance),
      grossRevenuesFormatted: formatMoney(values.grossRevenuesMostRecentYear),
      approximateNetIncomeFormatted: formatMoney(values.approximateNetIncome),
      inventoryValueFormatted: formatMoney(values.significantInventoryValue),
      currentAnnualRentFormatted: formatMoney(values.currentAnnualRent),
      monthlyPaymentsFormatted: formatMoney(values.existingObligationMonthlyPayments),
    },
  });

  const buildTransactionSummaryPrompt = () => {
    const summaryData = buildSummaryData();
    return [
      "Write a transaction summary for this business loan request using only the provided form data.",
      "Use a lender-facing tone and call out missing or unknown items as pending instead of inventing facts.",
      `Business: ${summaryData.businessName || "Pending"}`,
      `Property address: ${summaryData.propertyAddress || "Pending"}`,
      `Loan request: ${inferRequestedAmountOrLtv(loan) || "Pending"}`,
      `Loan product: ${formatOption(values.requestedLoanProduct) || "Pending"}`,
      `Property type: ${formatOption(values.propertyType) || "Pending"}`,
      `Appraisal status: ${formatOption(values.appraisalStatus) || "Pending"}`,
      `Current lender: ${values.currentLender || "Pending"}`,
      `Payoff balance: ${formatMoney(values.payoffBalance) || "Pending"}`,
      `Liens or encumbrances: ${values.propertyLiens || "Pending"}`,
      `Occupancy: ${formatOption(values.occupancyType) || "Pending"}`,
      `Use of proceeds: ${values.intendedUseOfProceeds || "Pending"}`,
      `Desired term and amortization: ${values.desiredLoanTermAndAmortization || "Pending"}`,
      `Guarantor/operator notes: ${[
        values.guarantorOwnershipAndRoles,
        values.guarantorBusinessInvolvement,
        values.guarantorOutsideIncome,
        values.primaryOperatorIndustryYears,
        values.operatorLocationCount,
      ].filter(Boolean).join("; ") || "Pending"}`,
      `Entity and tax return notes: ${[
        values.operatingBusinessLegalName,
        values.operatingBusinessEin,
        values.realEstateHoldingEntityLegalName,
        values.realEstateHoldingEntityEin,
        values.entityTypes,
        values.businessNature,
        formatOption(values.businessTaxReturnsAvailable),
        formatOption(values.personalTaxReturnsAvailable),
      ].filter(Boolean).join("; ") || "Pending"}`,
      `Financial notes: ${[
        values.grossRevenuesMostRecentYear ? `gross revenue ${formatMoney(values.grossRevenuesMostRecentYear)}` : "",
        values.approximateNetIncome ? `net income ${formatMoney(values.approximateNetIncome)}` : "",
        values.significantInventoryValue ? `inventory ${formatMoney(values.significantInventoryValue)}` : "",
        values.underwritingAddBacks,
        values.currentAnnualRent ? `annual rent ${formatMoney(values.currentAnnualRent)}` : "",
      ].filter(Boolean).join("; ") || "Pending"}`,
      `Debt and timing notes: ${[
        values.currentBusinessDebts,
        values.existingObligationMonthlyPayments ? `monthly debt payments ${formatMoney(values.existingObligationMonthlyPayments)}` : "",
        values.debtPaidOffWithTransaction,
        values.holdingEntityLineOfCreditOrLiens,
        values.targetClosingDateOrTimeSensitivity,
        values.appraisalOrderedExpectedDelivery,
        values.rentRollOrLeaseAgreementStatus,
      ].filter(Boolean).join("; ") || "Pending"}`,
    ].join("\n");
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const stripMarkdown = (value: string) =>
    value.replace(/^#{1,6}\s*/, "").replace(/\*\*/g, "").trim();

  const renderInline = (value: string) =>
    escapeHtml(value).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  const isKnownSectionTitle = (value: string) =>
    [
      "Loan Structure",
      "Use of Proceeds",
      "Cash Flow & DSCR Analysis",
      "Borrowers / Guarantors",
      "Operating Entities",
      "Collateral",
      "Key Income Summary",
      "Underwriting Notes",
      "Entity & Tax Returns",
      "Financial Performance",
      "Existing Debt & Obligations",
      "Timing & Other",
    ].includes(stripMarkdown(value));

  const isTableLine = (value: string) => value.trim().includes("|");

  const isTableSeparator = (value: string) =>
    /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(value.trim());

  const parseTableCells = (value: string) =>
    value
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => cell.trim());

  const isNumericCell = (value: string) =>
    /^~?\$/.test(value) ||
    /^\(?[\d,.]+%?\)?$/.test(value) ||
    /^(TBD|N\/A)$/i.test(value);

  const renderTable = (rows: string[]) => {
    const parsedRows = rows
      .filter((row) => !isTableSeparator(row))
      .map(parseTableCells)
      .filter((cells) => cells.some(Boolean));

    if (!parsedRows.length) return "";

    const [header, ...bodyRows] = parsedRows;
    const columnCount = header.length || 1;
    const width = `${Math.floor(100 / columnCount)}%`;

    return `<table>
      <thead>
        <tr>
          ${header
            .map((cell) => `<th style="width:${width}">${renderInline(cell)}</th>`)
            .join("")}
        </tr>
      </thead>
      <tbody>
        ${bodyRows
          .map(
            (row) => {
              const rowClass = /^total$/i.test(row[0] || "") ? " class=\"total\"" : "";
              return `<tr${rowClass}>${row
                .map((cell, index) => {
                  const alignClass = index > 0 && isNumericCell(cell) ? " class=\"num\"" : "";
                  return `<td${alignClass}>${renderInline(cell)}</td>`;
                })
                .join("")}</tr>`;
            },
          )
          .join("")}
      </tbody>
    </table>`;
  };

  const renderSummaryHtml = (summary: string) => {
    const lines = summary.replace(/\r\n/g, "\n").split("\n");
    const firstContentIndex = lines.findIndex((line) => line.trim());
    const firstContent =
      firstContentIndex >= 0 ? stripMarkdown(lines[firstContentIndex]) : "";
    const title =
      firstContent &&
      !firstContent.includes("|") &&
      !firstContent.includes(":") &&
      firstContent.length <= 90
        ? firstContent
        : "Commercial Real Estate Transaction Summary";
    const startIndex = title === firstContent ? firstContentIndex + 1 : 0;
    const subtitle = [businessName, formatAddress(business)]
      .filter(Boolean)
      .join("  |  ");
    const htmlParts = [
      `<h1>${escapeHtml(title)}</h1>`,
      subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : "",
      `<div class="title-rule"></div>`,
    ];

    for (let index = startIndex; index < lines.length; index += 1) {
      const line = lines[index].trim();
      if (!line || isTableSeparator(line)) continue;

      if (isTableLine(line)) {
        const tableRows = [line];
        while (index + 1 < lines.length && isTableLine(lines[index + 1])) {
          index += 1;
          tableRows.push(lines[index]);
        }
        htmlParts.push(renderTable(tableRows));
        continue;
      }

      if (/^#{1,3}\s+/.test(line) || isKnownSectionTitle(line)) {
        htmlParts.push(`<h2>${escapeHtml(stripMarkdown(line))}</h2>`);
        continue;
      }

      if (/^[-*]\s+/.test(line)) {
        htmlParts.push(`<p class="bullet">${renderInline(line.replace(/^[-*]\s+/, ""))}</p>`);
        continue;
      }

      const paragraphClass = /^note:/i.test(line) ? " class=\"note\"" : "";
      htmlParts.push(`<p${paragraphClass}>${renderInline(line)}</p>`);
    }

    return htmlParts.join("\n");
  };

  const downloadWordFile = (summary: string) => {
    const htmlBody = renderSummaryHtml(summary);
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    @page WordSection1 {
      size: 8.5in 11in;
      margin: 0.75in 0.75in 0.75in 0.75in;
    }
    div.WordSection1 { page: WordSection1; }
    body {
      font-family: Calibri, Arial, sans-serif;
      font-size: 10.5pt;
      color: #000000;
      line-height: 1.32;
    }
    h1 {
      color: #1F4E79;
      font-size: 18pt;
      font-weight: 700;
      margin: 8pt 0 2pt;
    }
    .subtitle {
      color: #555555;
      font-size: 10.5pt;
      margin: 0 0 4pt;
    }
    .title-rule {
      border-bottom: 2px solid #2E75B6;
      height: 1px;
      margin: 0 0 10pt;
    }
    h2 {
      color: #1F4E79;
      font-size: 13pt;
      font-weight: 700;
      border-bottom: 1px solid #2E75B6;
      padding-bottom: 2pt;
      margin: 14pt 0 6pt;
    }
    p {
      margin: 5pt 0;
      line-height: 1.35;
    }
    p.note {
      color: #444444;
      font-style: italic;
    }
    p.bullet {
      margin-left: 16pt;
      text-indent: -10pt;
    }
    p.bullet:before {
      content: "• ";
    }
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 5pt 0 10pt;
      table-layout: fixed;
    }
    th {
      background: #D6E4F0;
      color: #1F4E79;
      font-weight: 700;
      border: 1px solid #CCCCCC;
      padding: 4pt 6pt;
      text-align: left;
      vertical-align: top;
    }
    td {
      border: 1px solid #CCCCCC;
      padding: 4pt 6pt;
      vertical-align: top;
    }
    td.num {
      text-align: right;
      white-space: nowrap;
    }
    tr.total td {
      background: #F5F5F5;
      font-weight: 700;
    }
  </style>
</head>
<body>
  <div class="WordSection1">
    ${htmlBody}
  </div>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const fileName = `${businessName || "transaction"}-summary.doc`
      .replace(/[^a-z0-9._-]+/gi, "-")
      .replace(/-+/g, "-");
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleGenerateSummary = async () => {
    if (!loan?.id) {
      setSummaryError("Save the loan before generating a transaction summary.");
      return;
    }

    setGenerating(true);
    setGenerationElapsedSeconds(0);
    setSummaryError("");
    try {
      const response = await generateTransactionSummary(loan.id, {
        prompt: buildTransactionSummaryPrompt(),
        dealData: buildSummaryData(),
      });
      const summary = response?.summary || "";
      if (!summary) {
        setSummaryError("Gemini returned an empty transaction summary.");
        return;
      }
      downloadWordFile(summary);
    } catch (error: any) {
      setSummaryError(
        error?.message ||
          "Unable to generate transaction summary. Check Gemini configuration.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!business?.id) return;

    setSaving(true);
    try {
      await updateCustomerBusiness(business.id, {
        propertyType: values.propertyType,
        appraisalStatus: values.appraisalStatus,
        appraiserName: values.appraiserName,
        currentLender: values.currentLender,
        payoffBalance: values.payoffBalance ? Number(values.payoffBalance) : null,
        propertyLiens: values.propertyLiens,
        occupancyType: values.occupancyType,
      });

      if (loan?.id) {
        await updateCustomerLoanRequest(loan.id, {
          requestedLoanProduct: values.requestedLoanProduct,
          intendedUseOfProceeds: values.intendedUseOfProceeds,
          desiredLoanTermAndAmortization:
            values.desiredLoanTermAndAmortization,
          guarantorOwnershipAndRoles: values.guarantorOwnershipAndRoles,
          guarantorBusinessInvolvement:
            values.guarantorBusinessInvolvement,
          guarantorOutsideIncome: values.guarantorOutsideIncome,
          primaryOperatorIndustryYears:
            values.primaryOperatorIndustryYears,
          operatorLocationCount: values.operatorLocationCount,
          guarantorCreditEvents: values.guarantorCreditEvents,
          operatingBusinessLegalName:
            values.operatingBusinessLegalName,
          operatingBusinessEin: values.operatingBusinessEin,
          realEstateHoldingEntityLegalName:
            values.realEstateHoldingEntityLegalName,
          realEstateHoldingEntityEin:
            values.realEstateHoldingEntityEin,
          entityTypes: values.entityTypes,
          businessNature: values.businessNature,
          businessTaxReturnsAvailable:
            values.businessTaxReturnsAvailable,
          personalTaxReturnsAvailable:
            values.personalTaxReturnsAvailable,
          otherGuarantorBusinesses:
            values.otherGuarantorBusinesses,
          grossRevenuesMostRecentYear: values.grossRevenuesMostRecentYear
            ? Number(values.grossRevenuesMostRecentYear)
            : null,
          approximateNetIncome: values.approximateNetIncome
            ? Number(values.approximateNetIncome)
            : null,
          significantInventoryValue: values.significantInventoryValue
            ? Number(values.significantInventoryValue)
            : null,
          underwritingAddBacks: values.underwritingAddBacks,
          currentAnnualRent: values.currentAnnualRent
            ? Number(values.currentAnnualRent)
            : null,
          currentBusinessDebts: values.currentBusinessDebts,
          existingObligationMonthlyPayments:
            values.existingObligationMonthlyPayments
              ? Number(values.existingObligationMonthlyPayments)
              : null,
          debtPaidOffWithTransaction:
            values.debtPaidOffWithTransaction,
          holdingEntityLineOfCreditOrLiens:
            values.holdingEntityLineOfCreditOrLiens,
          targetClosingDateOrTimeSensitivity:
            values.targetClosingDateOrTimeSensitivity,
          appraisalOrderedExpectedDelivery:
            values.appraisalOrderedExpectedDelivery,
          rentRollOrLeaseAgreementStatus:
            values.rentRollOrLeaseAgreementStatus,
        });
      }

      onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "calc(100% - 64px)" },
          maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          pr: 7,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "white",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={fullScreen ? "h6" : "h5"}
            component="span"
            sx={{ fontWeight: 700, color: "#1f2937", display: "block" }}
          >
            Property, Collateral & Loan Request
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {businessName}
          </Typography>
        </Box>
        {!generating && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={saving}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent
        dividers={false}
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          bgcolor: "#f8fafc",
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            bgcolor: "white",
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "#4b5563",
          },
          "& .MuiInputLabel-root": {
            lineHeight: 1.25,
          },
        }}
      >
        {generating ? (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.light",
              bgcolor: "white",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Typography fontWeight={700} color="primary.dark" sx={{ mb: 1 }}>
              Generating transaction summary
            </Typography>
            <LinearProgress
              variant="determinate"
              value={generationProgress}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "#dbeafe",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                },
              }}
            />
          </Box>
        ) : (
          <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                border: "1px solid",
                borderColor: "primary.light",
                borderRadius: 2,
                bgcolor: "#eff6ff",
              }}
            >
              <Typography fontWeight={700} color="primary.dark">
              Please answer these property and collateral questions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Saved answers are reused the next time this loan package is opened.
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="1. What is the property address?"
              value={formatAddress(business)}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>2. Property Type</InputLabel>
              <Select
                name="propertyType"
                value={values.propertyType}
                onChange={handleChange}
                label="2. Property Type"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="retail">Retail</MenuItem>
                <MenuItem value="mixed-use">Mixed-use</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="industrial">Industrial</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>3. Appraisal Status</InputLabel>
              <Select
                name="appraisalStatus"
                value={values.appraisalStatus}
                onChange={handleChange}
                label="3. Do you have an appraisal in hand, or is this an expected/estimated value?"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="in-hand">Appraisal in hand</MenuItem>
                <MenuItem value="expected">Expected value</MenuItem>
                <MenuItem value="estimated">Estimated value</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Who is the appraiser?"
              name="appraiserName"
              value={values.appraiserName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="4. What is the current lender and approximate payoff balance?"
              name="currentLender"
              value={values.currentLender}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Approximate payoff balance"
              name="payoffBalance"
              value={values.payoffBalance}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="5. Are there any other liens, second mortgages, or encumbrances on the property?"
              name="propertyLiens"
              value={values.propertyLiens}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>6. Is the property owner-occupied, tenant-occupied, or both?</InputLabel>
              <Select
                name="occupancyType"
                value={values.occupancyType}
                onChange={handleChange}
                label="6. Occupancy"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="owner-occupied">Owner-occupied</MenuItem>
                <MenuItem value="tenant-occupied">Tenant-occupied</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Loan Request
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="7. What is the requested loan amount or target LTV?"
              value={inferRequestedAmountOrLtv(loan)}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>8. Loan Product</InputLabel>
              <Select
                name="requestedLoanProduct"
                value={values.requestedLoanProduct}
                onChange={handleChange}
                label="8. Loan Product"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="conventional_cre">Conventional CRE</MenuItem>
                <MenuItem value="sba_7a">SBA 7(a)</MenuItem>
                <MenuItem value="sba_504">SBA 504</MenuItem>
                <MenuItem value="bridge">Bridge</MenuItem>
                <MenuItem value="conventional">Conventional</MenuItem>
                <MenuItem value="SBA">SBA</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="9. What is the intended use of proceeds - payoff only, or cash-out? If cash-out, how will it be used?"
              name="intendedUseOfProceeds"
              value={values.intendedUseOfProceeds}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="10. What is the desired loan term and amortization period?"
              name="desiredLoanTermAndAmortization"
              value={values.desiredLoanTermAndAmortization}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Borrower / Guarantors
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="11. Guarantor full legal names"
              value={loan?.guaranteedName || ""}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Ownership percentages and roles"
              name="guarantorOwnershipAndRoles"
              value={values.guarantorOwnershipAndRoles}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="12. Is each guarantor active in the business or passive?"
              name="guarantorBusinessInvolvement"
              value={values.guarantorBusinessInvolvement}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="13. Does any guarantor have outside W-2 income? If so, from what employer and approximately how much?"
              name="guarantorOutsideIncome"
              value={values.guarantorOutsideIncome}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="14. How many years has the primary operator been in this industry?"
              name="primaryOperatorIndustryYears"
              value={values.primaryOperatorIndustryYears}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="15. How many locations does the operator currently run?"
              name="operatorLocationCount"
              value={values.operatorLocationCount}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="16. Are there any prior bankruptcies, foreclosures, or judgments we should be aware of?"
              name="guarantorCreditEvents"
              value={values.guarantorCreditEvents}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Entity & Tax Returns
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="17. Operating business legal name"
              name="operatingBusinessLegalName"
              value={values.operatingBusinessLegalName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="17. Operating business EIN"
              name="operatingBusinessEin"
              value={values.operatingBusinessEin}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="18. Real estate holding entity legal name"
              name="realEstateHoldingEntityLegalName"
              value={values.realEstateHoldingEntityLegalName}
              onChange={handleChange}
              placeholder="If separate"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="18. Real estate holding entity EIN"
              name="realEstateHoldingEntityEin"
              value={values.realEstateHoldingEntityEin}
              onChange={handleChange}
              placeholder="If separate"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="19. What type of entities are these (S-Corp, LLC, Partnership, C-Corp)?"
              name="entityTypes"
              value={values.entityTypes}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="20. What is the nature of the business (retail, restaurant, service, etc.)?"
              name="businessNature"
              value={values.businessNature}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>21. Business tax returns</InputLabel>
              <Select
                name="businessTaxReturnsAvailable"
                value={values.businessTaxReturnsAvailable}
                onChange={handleChange}
                label="21. Business tax returns"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="partial">Partial / in progress</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>22. Personal tax returns</InputLabel>
              <Select
                name="personalTaxReturnsAvailable"
                value={values.personalTaxReturnsAvailable}
                onChange={handleChange}
                label="22. Personal tax returns"
              >
                <MenuItem value="">
                  <em>Not selected</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="partial">Partial / in progress</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="23. Are there any other businesses owned by the guarantors not reflected in these returns?"
              name="otherGuarantorBusinesses"
              value={values.otherGuarantorBusinesses}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Financial Performance
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="24. Gross revenues for the most recent full year"
              name="grossRevenuesMostRecentYear"
              value={values.grossRevenuesMostRecentYear}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="25. Approximate net income or ordinary business income"
              name="approximateNetIncome"
              value={values.approximateNetIncome}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              type="number"
              label="26. Does the business carry significant inventory? What is the current value?"
              name="significantInventoryValue"
              value={values.significantInventoryValue}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="27. Are there any large one-time expenses or add-backs that should be noted in underwriting?"
              name="underwritingAddBacks"
              value={values.underwritingAddBacks}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              type="number"
              label="28. Current annual rent being paid"
              name="currentAnnualRent"
              value={values.currentAnnualRent}
              onChange={handleChange}
              placeholder="If owner-occupied, enter rent paid to holding LLC"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Existing Debt & Obligations
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="29. What are all current business debts (SBA loans, lines of credit, equipment financing)?"
              name="currentBusinessDebts"
              value={values.currentBusinessDebts}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              type="number"
              label="30. Monthly payments on existing obligations"
              name="existingObligationMonthlyPayments"
              value={values.existingObligationMonthlyPayments}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="31. Will any existing debt be paid off as part of this transaction?"
              name="debtPaidOffWithTransaction"
              value={values.debtPaidOffWithTransaction}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="32. Does the holding entity have a line of credit or any other liens?"
              name="holdingEntityLineOfCreditOrLiens"
              value={values.holdingEntityLineOfCreditOrLiens}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography sx={sectionTitleSx}>
              Timing & Other
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="33. Is there a target closing date or any time sensitivity (lease expiration, rate lock, seasonal need)?"
              name="targetClosingDateOrTimeSensitivity"
              value={values.targetClosingDateOrTimeSensitivity}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="34. Has an appraisal been ordered? If so, what is the expected delivery date?"
              name="appraisalOrderedExpectedDelivery"
              value={values.appraisalOrderedExpectedDelivery}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="35. Is there a current rent roll or lease agreement between the operating entity and the holding LLC?"
              name="rentRollOrLeaseAgreementStatus"
              value={values.rentRollOrLeaseAgreementStatus}
              onChange={handleChange}
            />
          </Grid>

          {summaryError && (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "error.light",
                  bgcolor: "#fef2f2",
                  color: "error.dark",
                  fontSize: "0.875rem",
                }}
              >
                {summaryError}
              </Box>
            </Grid>
          )}

        </Grid>
        )}
      </DialogContent>
      {!generating && (
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          gap: 1,
          bgcolor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > :not(style) ~ :not(style)": {
            ml: { xs: 0, sm: 1 },
          },
        }}
      >
        <Button
          variant="outlined"
          onClick={handleGenerateSummary}
          startIcon={<ArticleIcon />}
          disabled={saving || generating}
          fullWidth={fullScreen}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {generating ? "Generating..." : "Generate Summary"}
        </Button>
        <Button
          color="error"
          variant="outlined"
          onClick={onClose}
          startIcon={<CloseIcon />}
          disabled={saving || generating}
          fullWidth={fullScreen}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          loadingPosition="start"
          startIcon={<SaveIcon />}
          fullWidth={fullScreen}
          sx={{ borderRadius: 2, fontWeight: 700, minWidth: { sm: 160 } }}
        >
          {loan?.id ? "Update Answers" : "Save Answers"}
        </LoadingButton>
      </DialogActions>
      )}
    </Dialog>
  );
};

export default PropertyCollateralQuestionsDialog;
